"use client";

import { useRef } from "react";
import {
  sectionName,
  stopPositions,
  PLAYED_THROUGH_RATIO,
  type PieceSection,
  type SectionState,
} from "../piece";
import {
  KEYBOARD_MAX_W_PX,
  TRACE_EDGE,
  TRACE_INK,
  TRACE_MARK,
  TRACE_OFF,
  TRACE_SEL,
} from "./panel-metrics";

/**
 * The piece, as one horizontal object: its map, its report, and its control.
 *
 * ## It is what replaces the stepper
 *
 * In phase 1 the progression is **behind** the player — five steps crossed
 * inside one unit — so it lives in the footer, under the action. In phase 2
 * it is **in front** of them — a piece to cross — so it lives in the body,
 * above the action. That displacement is the change of level; the footer
 * simply goes, and `MiniSteps` would be a 46 px lie here, counting five steps
 * of a unit that no longer exists.
 *
 * It is also the only thing on the screen that says where the player is
 * inside their piece: the roll shows about **3% of a traversal** at a time.
 *
 * ## Why the section and never the attack
 *
 * At 504 px of rail, one cell per attack gives **0.95 px** on a median piece
 * of 531 attacks, 0.40 at the p90, **0.068 at the corpus maximum**. That is
 * not a small object, it is no object. The section gives 72 px (42 to 126
 * across the common range), which is legible, nameable and clickable — and
 * **76.0% of pieces (152 / 200) have between 3 and 12 sections**.
 *
 * `RunMap` therefore does **not** generalise to a whole piece, and must not
 * be reached for with 531 groups.
 *
 * ## Three fills, and a fourth state through form
 *
 * Not read, read and held, read with holes — solid fill, solid edge, dashed
 * edge. Differences of **shape**, not of luminance, so they survive
 * achromatopsia; and they avoid a fourth grey, which could not clear the 3:1
 * WCAG 1.4.11 asks against the other three on this ground. See the scale in
 * `panel-metrics.ts`.
 *
 * A stop is drawn as a **rule across the rail**, not as a cell state: a stop
 * is an instant, and it belongs to no section.
 */

const RAIL_H_PX = 26;
const RAIL_GAP_PX = 3;
const RAIL_RADIUS_PX = 6;
const RULER_H_PX = 8;

/** A cell narrower than this stops being a target. */
const MIN_CELL_PX = 28;

/** How far the stop rule overflows the rail, top and bottom. */
const STOP_OVERHANG_PX = 4;


export type PieceRailProps = {
  readonly cells: readonly PieceSection[];
  readonly states: readonly SectionState[];
  /** `null` selects the whole piece. */
  readonly selected: string | null;
  readonly onSelect: (id: string | null) => void;
  /** Ticks where the last traversal came off the rails. */
  readonly stops: readonly { readonly startTick: number }[];
  readonly ticksPerBar: number;
  /** Live position while a pass runs, in ticks. `null` at rest. */
  readonly playedTick?: number | null;
  /**
   * Whether the cells report on a crossing at all.
   *
   * `false` once the piece is kept: the rail stops being a dashboard and the
   * accessible name of a cell falls back to its place. Without this, every
   * cell said « — joué » to a screen reader, including the ones a traversal
   * stopped in, which is the opposite of the fact.
   */
  readonly verdict?: boolean;
};

export function PieceRail({
  cells,
  states,
  selected,
  onSelect,
  stops,
  ticksPerBar,
  playedTick = null,
  verdict = true,
}: PieceRailProps) {
  const ref = useRef<HTMLDivElement>(null);

  if (cells.length === 0) {
    return null;
  }

  const startTick = cells[0].startTick;
  const endTick = cells[cells.length - 1].endTick;
  const widths = cellWidths(cells);
  const marks = stopPositions(stops, startTick, endTick);
  const position =
    playedTick === null || endTick <= startTick
      ? null
      : Math.min(1, Math.max(0, (playedTick - startTick) / (endTick - startTick)));

  // Roving tabindex: one stop on the rail, arrows inside it. A radio group of
  // twelve cells that each took a tab stop would put the primary action twelve
  // presses away.
  const focusIndex = Math.max(
    0,
    cells.findIndex((cell) => cell.id === selected),
  );

  const move = (delta: number) => {
    const next = cells[focusIndex + delta];

    if (next === undefined) {
      return;
    }

    onSelect(next.id);
    ref.current
      ?.querySelectorAll<HTMLButtonElement>("[data-rail-cell]")
      [focusIndex + delta]?.focus();
  };

  return (
    <div style={{ marginBottom: 4 }}>
      <div
        ref={ref}
        role="radiogroup"
        aria-label="Les parties du morceau"
        onKeyDown={(event) => {
          if (event.key === "ArrowRight") {
            event.preventDefault();
            move(1);

            return;
          }

          if (event.key === "ArrowLeft") {
            event.preventDefault();
            move(-1);
          }
        }}
        style={{
          position: "relative",
          display: "flex",
          gap: RAIL_GAP_PX,
          width: "100%",
          height: RAIL_H_PX,
        }}
      >
        {cells.map((cell, index) => (
          <Cell
            key={cell.id}
            cell={cell}
            state={states[index] ?? { honoured: null, stopped: false }}
            grow={widths[index]}
            selected={selected === cell.id}
            tabbable={index === focusIndex}
            verdict={verdict}
            onSelect={() => onSelect(cell.id)}
          />
        ))}

        {marks.map((at, index) => (
          <span
            key={`stop-${index}`}
            aria-hidden
            style={{
              position: "absolute",
              left: `${at * 100}%`,
              top: -STOP_OVERHANG_PX,
              height: RAIL_H_PX + STOP_OVERHANG_PX * 2,
              width: 2,
              borderRadius: 1,
              background: TRACE_MARK,
            }}
          />
        ))}

        {position !== null && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: `${position * 100}%`,
              top: -STOP_OVERHANG_PX,
              height: RAIL_H_PX + STOP_OVERHANG_PX * 2,
              width: 2,
              borderRadius: 1,
              background: TRACE_MARK,
            }}
          />
        )}
      </div>

      <Ruler
        startTick={startTick}
        endTick={endTick}
        ticksPerBar={ticksPerBar}
        startMeasure={cells[0].startMeasure}
      />
    </div>
  );
}

function Cell({
  cell,
  state,
  grow,
  selected,
  tabbable,
  verdict,
  onSelect,
}: {
  cell: PieceSection;
  state: SectionState;
  grow: number;
  selected: boolean;
  tabbable: boolean;
  verdict: boolean;
  onSelect: () => void;
}) {
  const read = state.honoured !== null;
  const held = read && state.honoured! >= PLAYED_THROUGH_RATIO;

  return (
    <button
      type="button"
      data-rail-cell
      role="radio"
      aria-checked={selected}
      tabIndex={tabbable ? 0 : -1}
      title={sectionName(cell)}
      aria-label={verdict ? cellLabel(cell, state) : place(cell)}
      onClick={onSelect}
      className="transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-[var(--color-accent)]"
      style={{
        flexGrow: grow,
        flexBasis: 0,
        minWidth: MIN_CELL_PX,
        height: RAIL_H_PX,
        padding: 0,
        borderRadius: RAIL_RADIUS_PX,
        // Three states, three **forms**, because a fourth grey could not clear
        // 3:1 against the other three on this ground: solid fill for a part
        // that held, a dashed edge for one the traversal went through without
        // answering, a solid edge for one it never reached. Dashed against
        // solid survives achromatopsia, where two alphas 0.08 apart do not.
        background: held ? TRACE_INK : TRACE_OFF,
        border: held
          ? "1px solid transparent"
          : `1px ${read ? "dashed" : "solid"} ${read ? TRACE_INK : TRACE_EDGE}`,
        // The ring sits on the ground rather than on the fill, which is what
        // keeps it at 8.9:1 instead of 2.5:1 against a filled cell.
        outline: selected ? `1px solid ${TRACE_SEL}` : "none",
        outlineOffset: 2,
        cursor: "pointer",
      }}
    />
  );
}

/**
 * Bars under the rail.
 *
 * Not decoration: `RunMap` established that proportional widths only read as
 * durations when a measured channel says so — without it, uneven cells read
 * as a misalignment.
 */
function Ruler({
  startTick,
  endTick,
  ticksPerBar,
  startMeasure,
}: {
  startTick: number;
  endTick: number;
  ticksPerBar: number;
  startMeasure: number;
}) {
  const span = endTick - startTick;

  if (span <= 0 || ticksPerBar <= 0) {
    return null;
  }

  const bars = Math.round(span / ticksPerBar);
  // One tick every whole number of bars, chosen so two ticks never fall
  // closer than ~24 px on a 504 px rail.
  const every = Math.max(1, Math.ceil(bars / (KEYBOARD_MAX_W_PX / 24)));
  const marks: { left: number; measure: number }[] = [];

  for (let bar = 0; bar <= bars; bar += every) {
    marks.push({
      left: ((bar * ticksPerBar) / span) * 100,
      measure: startMeasure + bar,
    });
  }

  return (
    <div
      aria-hidden
      style={{ position: "relative", height: RULER_H_PX, marginTop: 3 }}
    >
      {marks.map((mark) => (
        <span
          key={mark.measure}
          style={{
            position: "absolute",
            left: `${Math.min(100, mark.left)}%`,
            top: 0,
            width: 1,
            height: RULER_H_PX,
            background: TRACE_EDGE,
          }}
        />
      ))}
    </div>
  );
}

/** Where a cell is, with no claim about how it went. */
function place(cell: PieceSection): string {
  return `${sectionName(cell)}, mesures ${cell.startMeasure} à ${cell.endMeasure}`;
}

/** What a screen reader is told about one cell. */
function cellLabel(cell: PieceSection, state: SectionState): string {
  const where = place(cell);

  if (state.stopped) {
    return `${where} — tu t’es arrêté ici`;
  }

  if (state.honoured === null) {
    return where;
  }

  return state.honoured >= PLAYED_THROUGH_RATIO
    ? `${where} — joué`
    : `${where} — il manquait des notes`;
}

/**
 * How much of the rail each cell takes: its own duration, with a floor so a
 * short section stays a target.
 */
function cellWidths(cells: readonly PieceSection[]): readonly number[] {
  return cells.map((cell) => Math.max(1, cell.endTick - cell.startTick));
}
